import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { LoadingScreen } from '@/components/layout/LoadingScreen';
import { Button } from '@/components/ui/Button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Label } from '@/components/ui/Label';
import {
  getGoogleMarketingProvider,
  googleMarketingRedirectUri,
  type GoogleMarketingProvider,
} from '@/features/integrations/googleMarketingOAuth';

/**
 * Shared OAuth redirect handler for Search Console and YouTube.
 *
 * Both follow the same three-step shape as GA4 — exchange the code, let the
 * operator pick which property/channel to sync, then trigger a first sync —
 * so they share this page and differ only in the Edge Functions called and
 * the labels rendered. Keeping the resource pick separate from the code
 * exchange means an operator can re-pick later without redoing the consent
 * flow.
 */

interface ResourceOption {
  /** Value persisted by the set-resource function. */
  value: string;
  label: string;
  /** Secondary detail line in the dropdown. */
  detail?: string;
}

type Phase =
  | { kind: 'exchanging' }
  | { kind: 'picking'; options: ResourceOption[]; selected: string }
  | { kind: 'finalizing' }
  | { kind: 'error'; message: string };

interface GoogleMarketingCallbackPageProps {
  provider: GoogleMarketingProvider;
}

export function GoogleMarketingCallbackPage({ provider }: GoogleMarketingCallbackPageProps) {
  const config = getGoogleMarketingProvider(provider);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>({ kind: 'exchanging' });
  // React 18 StrictMode mounts effects twice in dev and the OAuth `code` is
  // single-use, so the second run would always fail and clobber the result.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const code = params.get('code');
    const state = params.get('state');
    const oauthError = params.get('error') ?? params.get('error_description');

    if (oauthError) {
      setPhase({ kind: 'error', message: `Google rejected the connection: ${oauthError}` });
      return;
    }
    if (!code) {
      setPhase({ kind: 'error', message: 'Missing OAuth code from Google.' });
      return;
    }

    const expectedState = sessionStorage.getItem(config.stateStorageKey);
    if (!expectedState || !state || state !== expectedState) {
      setPhase({
        kind: 'error',
        message:
          'OAuth state mismatch. For your security, please retry the connection from the Integrations page.',
      });
      return;
    }
    sessionStorage.removeItem(config.stateStorageKey);

    (async () => {
      const { data, error: fnError } = await supabase.functions.invoke<CallbackResponse>(
        config.callbackFunction,
        {
          body: {
            code,
            state,
            redirect_uri: googleMarketingRedirectUri(provider),
          },
        },
      );
      if (fnError) {
        setPhase({ kind: 'error', message: await extractFunctionErrorMessage(fnError) });
        return;
      }

      const options = toOptions(provider, data);
      if (options.length === 0) {
        setPhase({ kind: 'error', message: emptyResourceMessage(provider) });
        return;
      }
      setPhase({ kind: 'picking', options, selected: options[0].value });
    })();
  }, [params, provider, config]);

  const finalize = async (value: string) => {
    setPhase({ kind: 'finalizing' });

    const { error: setErr } = await supabase.functions.invoke(config.setResourceFunction, {
      body: setResourceBody(provider, value),
    });
    if (setErr) {
      setPhase({ kind: 'error', message: await extractFunctionErrorMessage(setErr) });
      return;
    }

    toast.success(`${config.name} connected. Backfilling history…`);

    // Best-effort first sync: the integration is already marked connected and
    // the hourly cron will retry, so a failure here is a warning, not an error
    // state the operator has to resolve before leaving the page.
    const { error: syncError } = await supabase.functions.invoke(config.syncFunction, {
      body: {},
    });
    if (syncError) {
      toast.error(
        `Connected, but the initial sync failed: ${await extractFunctionErrorMessage(syncError)}`,
      );
    } else {
      toast.success(`${config.name} sync complete.`);
    }

    navigate('/integrations', { replace: true });
  };

  if (phase.kind === 'error') {
    return (
      <div className="flex flex-1 items-center justify-center p-12">
        <div className="space-y-4 text-center">
          <h1 className="text-xl font-medium tracking-headline">Connection failed</h1>
          <p className="max-w-lg text-sm text-muted-foreground">{phase.message}</p>
          <Button variant="outline" onClick={() => navigate('/integrations', { replace: true })}>
            Back to Integrations
          </Button>
        </div>
      </div>
    );
  }

  if (phase.kind === 'picking') {
    const { options, selected } = phase;
    return (
      <div className="flex flex-1 items-center justify-center p-12">
        <div className="w-full max-w-lg space-y-6 rounded-xl border border-border bg-card p-8 shadow-sm">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-bright-moss-dark" />
              <h1 className="text-xl font-medium tracking-headline">Google connected</h1>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="google-resource">{config.resourceLabel}</Label>
            <Select
              value={selected}
              onValueChange={(value) => setPhase({ kind: 'picking', options, selected: value })}
            >
              <SelectTrigger id="google-resource">
                <SelectValue placeholder={`Choose a ${config.resourceLabel}`} />
              </SelectTrigger>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <span className="font-medium">{option.label}</span>
                    {option.detail && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        {option.detail}
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => navigate('/integrations', { replace: true })}>
              Cancel
            </Button>
            <Button onClick={() => finalize(selected)} disabled={!selected}>
              Connect &amp; sync
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <LoadingScreen />;
}

interface CallbackResponse {
  ok?: boolean;
  error?: string;
  sites?: Array<{ siteUrl: string; permissionLevel?: string; isDomainProperty?: boolean }>;
  channels?: Array<{
    channelId: string;
    title: string;
    handle?: string | null;
    subscriberCount?: number | null;
    videoCount?: number;
  }>;
}

function toOptions(
  provider: GoogleMarketingProvider,
  data: CallbackResponse | null | undefined,
): ResourceOption[] {
  if (provider === 'search_console') {
    return (data?.sites ?? []).map((site) => ({
      value: site.siteUrl,
      // Domain properties are identified as `sc-domain:example.com`, which
      // reads as noise in a picker — show the bare domain but keep the raw
      // identifier as the value, since the API needs it verbatim.
      label: site.isDomainProperty ? site.siteUrl.replace(/^sc-domain:/, '') : site.siteUrl,
      detail: site.isDomainProperty ? 'Domain property' : 'URL prefix',
    }));
  }
  return (data?.channels ?? []).map((channel) => ({
    value: channel.channelId,
    label: channel.title,
    detail: [
      channel.handle ? `@${channel.handle}` : null,
      channel.subscriberCount != null
        ? `${channel.subscriberCount.toLocaleString()} subscribers`
        : null,
    ]
      .filter(Boolean)
      .join(' · '),
  }));
}

function setResourceBody(
  provider: GoogleMarketingProvider,
  value: string,
): Record<string, string> {
  return provider === 'search_console' ? { site_url: value } : { channel_id: value };
}

function emptyResourceMessage(provider: GoogleMarketingProvider): string {
  return provider === 'search_console'
    ? 'The Google account you signed in with has no verified Search Console properties. Sign in with an account that has at least Restricted access to the property and try again.'
    : 'The Google account you signed in with owns no YouTube channels. If the channel is a Brand Account, pick that identity on the Google consent screen rather than your personal account.';
}

async function extractFunctionErrorMessage(error: unknown): Promise<string> {
  const fallback = error instanceof Error ? error.message : 'Edge Function call failed';
  const ctx = (error as { context?: Response }).context;
  if (!ctx || typeof ctx.json !== 'function') return fallback;
  try {
    const body = (await ctx.clone().json()) as { error?: string };
    return body?.error ?? fallback;
  } catch {
    return fallback;
  }
}
